// Presence heartbeat hook (R16 W-B) — keeps the caller registered in
// `online_sessions` for the whole life of the page: immediate first beat on
// mount, 60 s interval + up to 15 s random jitter (thundering-herd spread)
// while visible, flat 60 s cadence while hidden, leave ONLY on `pagehide`.
//
// Bug 2026-07-03 (usuário operando aparecia OFFLINE no /admin/online): the
// old contract sent {action:"leave"} on visibilitychange→hidden — the
// backend DELETEs the online_sessions row — and paused beats while hidden,
// so switching tabs/apps or locking the screen while o robô operava wiped
// presence instantly and let the session decay (>180 s). Hidden tabs now
// keep a reduced flat cadence and only a real page exit tears down.
//
// Ref discipline ported verbatim: the sender keeps a stable identity across
// renders — otherwise the interval effect would tear down and restart
// (losing its phase) on every visibility flip or count update.
import { useCallback, useEffect, useRef } from "react";
import { tradersOnlineKey } from "@/lib/hooks/useTradersOnline";
import { writeCachedTradersOnline } from "@/lib/hooks/traders-online-cache";
import { useOptionalQueryClient } from "@/lib/query";
import { postPresenceHeartbeat, sendPresenceLeave } from "../lib/presence-api";
import { usePresencePageVisibility } from "./usePresencePageVisibility";

// Server freshness (ONLINE_FRESHNESS_SECONDS = 180 s) is 3x this cadence —
// keep both in sync or online users decay into "stale".
export const HEARTBEAT_INTERVAL_MS = 60_000;
export const HEARTBEAT_JITTER_MS = 15_000;
// Hidden-tab cadence: flat (no jitter) because browsers throttle hidden-tab
// timers to ~1 fire/min anyway — 60 s is the effective floor and stays well
// under the 180 s server window, so backgrounded robots never decay.
export const HIDDEN_HEARTBEAT_INTERVAL_MS = 60_000;

type PublishCount = (next: number) => void;
type SendHeartbeat = () => Promise<void>;

function usePublishSharedCount(): PublishCount {
  const queryClient = useOptionalQueryClient();
  return useCallback(
    (next: number) => {
      writeCachedTradersOnline(next);
      queryClient?.setQueryData(tradersOnlineKey, { count: next });
    },
    [queryClient],
  );
}

function useHeartbeatSender(publishCount: PublishCount): SendHeartbeat {
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const next = await postPresenceHeartbeat();
      if (mountedRef.current && next !== null) publishCount(next);
    } catch {
      // Best-effort: keep the last known count.
    } finally {
      inFlightRef.current = false;
    }
  }, [publishCount]);
}

function startVisibleHeartbeatLoop(sendHeartbeat: SendHeartbeat): () => void {
  const pendingJitterTimers = new Set<ReturnType<typeof setTimeout>>();

  // First heartbeat is IMMEDIATE (no jitter) so presence registers on entry.
  void sendHeartbeat();

  const interval = setInterval(() => {
    const jitter = Math.random() * HEARTBEAT_JITTER_MS;
    const timer = setTimeout(() => {
      pendingJitterTimers.delete(timer);
      void sendHeartbeat();
    }, jitter);
    pendingJitterTimers.add(timer);
  }, HEARTBEAT_INTERVAL_MS);

  return () => {
    clearInterval(interval);
    for (const timer of pendingJitterTimers) clearTimeout(timer);
    pendingJitterTimers.clear();
  };
}

// Hidden loop: NO immediate beat (the visible loop just sent one on the flip)
// and no jitter — hidden-timer throttling already staggers clients for free.
function startHiddenHeartbeatLoop(sendHeartbeat: SendHeartbeat): () => void {
  const interval = setInterval(() => {
    void sendHeartbeat();
  }, HIDDEN_HEARTBEAT_INTERVAL_MS);
  return () => clearInterval(interval);
}

function useHeartbeatInterval(
  isPageVisible: boolean,
  sendHeartbeat: SendHeartbeat,
): void {
  useEffect(
    () =>
      isPageVisible
        ? startVisibleHeartbeatLoop(sendHeartbeat)
        : startHiddenHeartbeatLoop(sendHeartbeat),
    [isPageVisible, sendHeartbeat],
  );
}

// Leave ONLY on real page exit (`pagehide`) — NEVER on visibilitychange:
// the backend DELETEs the online_sessions row on leave, so leaving on a tab
// switch/screen lock made operating users vanish from /admin/online (bug
// 2026-07-03). `pagehide` also precedes bfcache suspension, so a `pageshow`
// with `persisted` re-registers immediately after a bfcache restore.
function usePageExitLeave(sendHeartbeat: SendHeartbeat): void {
  useEffect(() => {
    const onPagehide = (): void => sendPresenceLeave();
    const onPageshow = (event: PageTransitionEvent): void => {
      if (event.persisted) void sendHeartbeat();
    };
    window.addEventListener("pagehide", onPagehide);
    window.addEventListener("pageshow", onPageshow);
    return () => {
      window.removeEventListener("pagehide", onPagehide);
      window.removeEventListener("pageshow", onPageshow);
    };
  }, [sendHeartbeat]);
}

/**
 * Keep the caller registered in `online_sessions` for the page's lifetime —
 * full cadence while visible, reduced flat cadence while hidden (so a robô
 * operando em aba oculta keeps counting as online), leave only on pagehide.
 * Mount ONCE in the authenticated shell (RequireAuth) — never on public
 * pages, never duplicated between mobile and desktop trees.
 *
 * @example
 * function OnlinePresenceHeartbeat() { useOnlinePresenceHeartbeat(); return null; }
 */
export function useOnlinePresenceHeartbeat(): void {
  const isPageVisible = usePresencePageVisibility();
  const publishCount = usePublishSharedCount();
  const sendHeartbeat = useHeartbeatSender(publishCount);
  useHeartbeatInterval(isPageVisible, sendHeartbeat);
  usePageExitLeave(sendHeartbeat);
}
